// public/js/features/announcements/announcements-page.js
// "What's new" archive (all users) + authoring (superadmin). See docs/announcements.md.
import { loadAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement } from './api.js';
import { renderBlocks, announcementDate } from './render.js';
import { refreshWhatsNewBadge } from './boot.js';
import { getCurrentUser, isAdminLevel } from '../../modules/rbac.js';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

let DATA = { posts: [], lastReadSeq: 0, latestSeq: 0 };
let isSuper = false;

export async function initAnnouncementsPage(container) {
  isSuper = isAdminLevel(getCurrentUser().groups || []);   // superadmin OR admin may author
  container.innerHTML = `
    <div class="container-fluid py-3" style="max-width:900px;">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="h4 mb-0"><i class="fa-solid fa-bullhorn me-2 text-primary"></i>What's new</h1>
        <div class="d-flex gap-2">
          ${isSuper ? `<button id="ann-new" class="btn btn-sm btn-primary"><i class="fa-solid fa-plus me-1"></i>New post</button>` : ''}
          <button id="ann-refresh" class="btn btn-sm btn-outline-secondary"><i class="fa-solid fa-rotate me-1"></i>Refresh</button>
        </div>
      </div>
      <div id="ann-body"><div class="text-muted py-4"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div></div>
    </div>`;
  container.querySelector('#ann-refresh')?.addEventListener('click', () => load(container));
  container.querySelector('#ann-new')?.addEventListener('click', () => openEditor(container, null));
  await load(container);
}

async function load(container) {
  const body = container.querySelector('#ann-body');
  try { DATA = await loadAnnouncements(); render(container); refreshWhatsNewBadge(); }
  catch { body.innerHTML = `<div class="alert alert-danger">Failed to load posts.</div>`; }
}

function render(container) {
  const body = container.querySelector('#ann-body');
  const posts = DATA.posts || [];
  if (!posts.length) {
    body.innerHTML = `<div class="text-center text-muted py-5"><i class="fa-regular fa-newspaper fa-2x mb-3 d-block"></i>No posts yet.</div>`;
    return;
  }
  body.innerHTML = posts.map(p => {
    const draft = p.status !== 'published';
    const audienceBadge = (isSuper && p.audience === 'superadmin') ? '<span class="badge text-bg-dark ms-2">Superadmins</span>' : '';
    const statusBadge = draft ? '<span class="badge text-bg-warning ms-2">Draft</span>' : '';
    return `
      <div class="card shadow-sm mb-3">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <h2 class="h5 mb-1">${esc(p.title)} <span class="text-muted fw-normal small">#${p.seq}</span>${audienceBadge}${statusBadge}</h2>
              <div class="text-muted small mb-2">${announcementDate(p)}</div>
            </div>
            ${isSuper ? `
              <div class="dropdown">
                <button class="btn btn-sm btn-link text-dark px-2" data-bs-toggle="dropdown" aria-label="Post actions">⋮</button>
                <ul class="dropdown-menu dropdown-menu-end shadow">
                  <li><a class="dropdown-item ann-edit" href="#" data-seq="${p.seq}">✏️ Edit</a></li>
                  ${draft ? `<li><a class="dropdown-item ann-publish" href="#" data-seq="${p.seq}">📣 Publish</a></li>`
                          : `<li><a class="dropdown-item ann-unpublish" href="#" data-seq="${p.seq}">🕶️ Unpublish</a></li>`}
                  <li><hr class="dropdown-divider"></li>
                  <li><a class="dropdown-item text-danger ann-delete" href="#" data-seq="${p.seq}">🗑️ Delete</a></li>
                </ul>
              </div>` : ''}
          </div>
          <div class="announcement-body">${renderBlocks(p.blocks)}</div>
        </div>
      </div>`;
  }).join('');

  if (!isSuper) return;
  const bySeq = (sel, fn) => body.querySelectorAll(sel).forEach(a => a.addEventListener('click', (e) => { e.preventDefault(); fn(Number(a.dataset.seq)); }));
  bySeq('.ann-edit', (seq) => openEditor(container, posts.find(p => p.seq === seq)));
  bySeq('.ann-publish', async (seq) => { await updateAnnouncement(seq, { status: 'published' }); await load(container); });
  bySeq('.ann-unpublish', async (seq) => { await updateAnnouncement(seq, { status: 'draft' }); await load(container); });
  bySeq('.ann-delete', async (seq) => { if (confirm(`Delete post #${seq}?`)) { await deleteAnnouncement(seq); await load(container); } });
}

// ---------------- Superadmin editor (create / edit) ----------------
function openEditor(container, post) {
  const editing = !!post;
  const state = {
    title: post?.title || '',
    audience: post?.audience || 'all',
    blocks: JSON.parse(JSON.stringify(post?.blocks || [{ type: 'paragraph', text: '' }])),
  };
  document.getElementById('annEditor')?.remove();

  const html = `
    <div class="modal fade" id="annEditor" tabindex="-1">
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">${editing ? `Edit post #${post.seq}` : 'New post'}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="row g-2 mb-3">
              <div class="col-sm-8">
                <label class="form-label small fw-semibold">Title</label>
                <input id="ae-title" class="form-control" maxlength="300" value="${esc(state.title)}" placeholder="e.g. Faster loading & improvements">
              </div>
              <div class="col-sm-4">
                <label class="form-label small fw-semibold">Audience</label>
                <select id="ae-audience" class="form-select">
                  <option value="all"${state.audience === 'all' ? ' selected' : ''}>Everyone</option>
                  <option value="superadmin"${state.audience === 'superadmin' ? ' selected' : ''}>Superadmins only</option>
                </select>
              </div>
            </div>
            <label class="form-label small fw-semibold">Content blocks</label>
            <div id="ae-blocks"></div>
            <div class="btn-group btn-group-sm mt-2">
              <button class="btn btn-outline-secondary ae-add" data-type="heading"><i class="fa-solid fa-heading me-1"></i>Heading</button>
              <button class="btn btn-outline-secondary ae-add" data-type="paragraph"><i class="fa-solid fa-paragraph me-1"></i>Text</button>
              <button class="btn btn-outline-secondary ae-add" data-type="list"><i class="fa-solid fa-list-ul me-1"></i>List</button>
              <button class="btn btn-outline-secondary ae-add" data-type="image"><i class="fa-solid fa-image me-1"></i>Image</button>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-outline-primary ae-save" data-status="draft">Save draft</button>
            <button type="button" class="btn btn-primary ae-save" data-status="published"><i class="fa-solid fa-paper-plane me-1"></i>${editing && post.status === 'published' ? 'Save & keep published' : 'Publish'}</button>
          </div>
        </div>
      </div>
    </div>`;
  document.body.insertAdjacentHTML('beforeend', html);
  const el = document.getElementById('annEditor');
  const modal = new bootstrap.Modal(el);
  el.addEventListener('hidden.bs.modal', () => el.remove(), { once: true });

  const blocksEl = el.querySelector('#ae-blocks');
  const renderEditorBlocks = () => {
    blocksEl.innerHTML = state.blocks.map((b, i) => blockEditorRow(b, i, state.blocks.length)).join('') ||
      '<div class="text-muted small fst-italic mb-2">No blocks — add one below.</div>';
    blocksEl.querySelectorAll('[data-act]').forEach(btn => btn.addEventListener('click', () => {
      const i = Number(btn.dataset.i), act = btn.dataset.act;
      if (act === 'del') state.blocks.splice(i, 1);
      else if (act === 'up' && i > 0) [state.blocks[i - 1], state.blocks[i]] = [state.blocks[i], state.blocks[i - 1]];
      else if (act === 'down' && i < state.blocks.length - 1) [state.blocks[i + 1], state.blocks[i]] = [state.blocks[i], state.blocks[i + 1]];
      syncFromInputs(); renderEditorBlocks();
    }));
  };
  const syncFromInputs = () => {
    blocksEl.querySelectorAll('[data-field]').forEach(inp => {
      const i = Number(inp.dataset.i), f = inp.dataset.field;
      if (f === 'items') state.blocks[i].items = inp.value.split('\n').map(s => s.trim()).filter(Boolean);
      else state.blocks[i][f] = inp.value;
    });
  };
  blocksEl.addEventListener('input', syncFromInputs);
  renderEditorBlocks();

  el.querySelectorAll('.ae-add').forEach(btn => btn.addEventListener('click', () => {
    syncFromInputs();
    const t = btn.dataset.type;
    state.blocks.push(t === 'list' ? { type: 'list', items: [] } : t === 'image' ? { type: 'image', url: '', caption: '' } : { type: t, text: '' });
    renderEditorBlocks();
  }));

  el.querySelectorAll('.ae-save').forEach(btn => btn.addEventListener('click', async () => {
    syncFromInputs();
    const payload = {
      title: el.querySelector('#ae-title').value.trim(),
      audience: el.querySelector('#ae-audience').value,
      blocks: state.blocks,
      status: btn.dataset.status,
    };
    if (!payload.title && !payload.blocks.length) { window.showToast?.('Add a title or a block', 'warning'); return; }
    btn.disabled = true;
    try {
      if (editing) await updateAnnouncement(post.seq, payload);
      else await createAnnouncement(payload);
      modal.hide();
      window.showToast?.(payload.status === 'published' ? 'Post published' : 'Draft saved', 'success');
      await load(container);
    } catch (e) { window.showToast?.(e?.message || 'Failed to save', 'danger'); btn.disabled = false; }
  }));

  modal.show();
}

function blockEditorRow(b, i, total) {
  const controls = `
    <div class="btn-group btn-group-sm ms-2 flex-shrink-0">
      <button class="btn btn-outline-secondary" data-act="up" data-i="${i}" ${i === 0 ? 'disabled' : ''} title="Move up"><i class="fa-solid fa-arrow-up"></i></button>
      <button class="btn btn-outline-secondary" data-act="down" data-i="${i}" ${i === total - 1 ? 'disabled' : ''} title="Move down"><i class="fa-solid fa-arrow-down"></i></button>
      <button class="btn btn-outline-danger" data-act="del" data-i="${i}" title="Remove"><i class="fa-solid fa-xmark"></i></button>
    </div>`;
  let input;
  if (b.type === 'heading') input = `<input class="form-control form-control-sm" data-field="text" data-i="${i}" value="${esc(b.text || '')}" placeholder="Heading">`;
  else if (b.type === 'paragraph') input = `<textarea class="form-control form-control-sm" data-field="text" data-i="${i}" rows="2" placeholder="Paragraph text">${esc(b.text || '')}</textarea>`;
  else if (b.type === 'list') input = `<textarea class="form-control form-control-sm" data-field="items" data-i="${i}" rows="3" placeholder="One item per line">${esc((b.items || []).join('\n'))}</textarea>`;
  else input = `
      <input class="form-control form-control-sm mb-1" data-field="url" data-i="${i}" value="${esc(b.url || '')}" placeholder="Image URL (https://…-static.s3…/announcements/…)">
      <input class="form-control form-control-sm" data-field="caption" data-i="${i}" value="${esc(b.caption || '')}" placeholder="Caption (optional)">`;
  const label = { heading: 'Heading', paragraph: 'Text', list: 'List', image: 'Image' }[b.type] || b.type;
  return `
    <div class="d-flex align-items-start mb-2">
      <span class="badge text-bg-light border me-2 mt-1" style="width:64px;flex-shrink:0;">${label}</span>
      <div class="flex-grow-1">${input}</div>
      ${controls}
    </div>`;
}
